import React, { useContext } from 'react';
import { Icon } from '@/components/ui/Icon';
import { initialsOf } from '@/components/console/Avatar';
import { avatarColor } from './MsgAvatar';
import { Composer } from './Composer';
import { useT } from '@/i18n/labels';
import { useAgentStore } from '@/stores/agentStore';
import { usePermission } from '@/hooks/usePermission';
import { isOwnerUnknown, lockedByOtherOwner } from '@/utils/ownership';
import { ConsoleContext } from '@/console/ConsoleContext';
import type { Conversation, MessageAttachment } from '@/types';

/** The two things the one bar can be addressed to. `customer` reaches a real person; `guidance` never leaves the team. */
export type AnswerBarMode = 'customer' | 'guidance';

/**
 * The state x owner rule, in ONE place. Customer mode only when a human holds the conversation AND that human is
 * the signed-in agent. Anything else - the bot handling it, an open consultation, an unknown owner - is guidance.
 * (Read-only and closed are decided before this is asked.) An owner that is not exactly `me` is NOT me: a missing
 * owner or a missing `me` can never become permission to write to the customer.
 */
export function answerBarMode(
  state: Conversation['state'],
  owner: string | null | undefined,
  me: string | null | undefined,
): AnswerBarMode {
  return state === 'HUMAN_TAKEOVER' && !!owner && !!me && owner === me ? 'customer' : 'guidance';
}

/**
 * Does the bar offer a COMPOSER at all (either mode)? Exactly the gates the component below applies before it renders
 * one, in the same order: not closed, the viewer holds workspace.handle, no other agent holds it, a human takeover with a
 * named holder, and an owner the row actually states. Anything else is a read-only line. Exported so other things that
 * act on the conversation toward the customer (the proposed replies under a bot note) are offered by the SAME rule and
 * cannot drift from it. UX only: the server re-checks every act.
 */
export function answerBarWritable(
  conv: Conversation,
  holder: string | null | undefined,
  me: string | null | undefined,
  canHandle: boolean,
): boolean {
  if (conv.state === 'CLOSED') return false;
  if (!canHandle) return false;
  if (lockedByOtherOwner(conv, holder ?? null, me ?? null)) return false;
  if (conv.state === 'HUMAN_TAKEOVER' && !holder) return false;
  if (isOwnerUnknown(conv)) return false;
  return true;
}

/** The SignalR/AgentHub refusal text for "this session is not yours / not there" - identical for both on purpose. */
const isRefusal = (err: unknown) => {
  const raw = err instanceof Error ? err.message : String(err ?? '');
  return raw.includes('Session not available');
};

interface AnswerBarProps {
  conv: Conversation;
  /** The PERSON's display name (not the thread label) - names the reader on the To: line. */
  customerName: string;
  /**
   * Who owns the conversation: the verified wire `ownerAgentId` (ChatView derives it; there is no `assignedAgentId`
   * fallback). `null` = nobody can be named; during HUMAN_TAKEOVER that FAILS CLOSED. Informational for the bar (it is
   * the render-phase evidence the chat view's tests spy on that no stale owner is ever handed down): the bar's lock and
   * mode read {@link AnswerBarProps.holder}, never this.
   */
  owner: string | null;
  /**
   * Who HOLDS the conversation for "may I act on it" (utils/ownership.ts holderId): the verified owner under private
   * ownership, the verified live controller in legacy shared mode (they differ when a teammate took over what someone
   * else had guided). `null` = nobody can be named / not yet verified; during HUMAN_TAKEOVER that FAILS CLOSED. REQUIRED,
   * with no default: it used to fall back to `owner`, which is the private-ownership meaning and the WRONG one in legacy
   * shared mode (the owner of a conversation a teammate took over is not who holds the customer) - a caller that forgot
   * it silently got the wrong lock. ChatView passes the verified holder.
   */
  holder: string | null;
  /** The signed-in agent's id, or null when nobody is signed in (then nothing is ever mine). */
  me: string | null;
  /**
   * `owner` is null because a named owner has not yet been confirmed by the server (the chat view's owner
   * verification), NOT because nobody owns it. The bar then says so, neutrally - the other-agent banner would be false
   * for the true owner during the round trip.
   */
  ownerUnverified?: boolean;
  /**
   * `owner` is null because a named owner could not be confirmed and NOTHING is pending any more (the re-read failed or
   * the row was hidden). Not "confirming", and not "another agent" either: a neutral line, no composer, no Take over.
   */
  ownerUnconfirmed?: boolean;
  onSendCustomer: (text: string, attachmentIds?: string[]) => void | Promise<void>;
  /** Guidance to the bot; `attachmentIds` are pictures for the bot (2026-10-20), `attachments` their display metadata. */
  onSendGuidance: (text: string, attachmentIds?: string[], attachments?: MessageAttachment[]) => void | Promise<void>;
  onTakeover: () => void;
  /** Customer mode only: the customer-visible typing dots (AgentHub.SignalAgentTyping). Never wired to guidance. */
  onTyping?: () => void;
  /**
   * Bot voice Phase D (2026-10-31) - guidance mode only: "a teammate is still writing guidance", so the customer's next
   * bot turn waits (AgentHub.NotifyGuidanceComposing). The Composer throttles it to once per 500 ms on text changes (under the 1 s the quiet gate needs).
   * When omitted, the bar calls the shared console hub itself for THIS conversation; outside the console shell (no hub)
   * nothing is sent. Never the customer typing signal.
   */
  onGuidanceComposing?: () => void;
  /** Called once when the server refused a send as "not your session" - C2 wires it to a row refresh. */
  onRefused?: (sessionId: string) => void;
  /**
   * 2026-10-28 - text for the composer (Edit on a proposed reply), tagged with the MODE it was asked for. It reaches the
   * composer only while the bar is still in that mode: text picked while the bar was guidance must never surface in the
   * customer composer after a flip (the same rule as the keyed composers below).
   */
  prefill?: { text: string; seq: number; mode: AnswerBarMode } | null;
  /** The composer applied `prefill` (pass-through of Composer.onPrefillApplied). */
  onPrefillApplied?: (seq: number) => void;
}

/**
 * ONE answer bar. Its target is decided by the conversation's STATE and OWNER and by nothing else - not by the
 * timeline filter, not by a local toggle. The "To:" line always names who will read what is typed.
 *
 *   closed                                  -> read-only text (no Reopen: a closed conversation is terminal)
 *   viewer lacks workspace.handle           -> read-only line, NO input, NO Take over (whoever owns it)
 *   another agent owns it / owner unknown
 *     while a human holds it                -> read-only banner, NO input
 *   HUMAN_TAKEOVER and I own it             -> green, to the customer (attachments kept)
 *   everything else                         -> red, internal guidance to the bot
 *   (consulting and routed to a teammate    -> the red guidance bar PLUS an informational "Routed to {name}" line: a
 *                                              router-targeted consultation nobody has answered is still TEAM SHARED,
 *                                              operator decision D3, so it is not read-only)
 *
 * It wraps the existing Composer (attachments, typing relay, send state) rather than replacing it.
 */
export const AnswerBar: React.FC<AnswerBarProps> = ({
  conv,
  customerName,
  holder,
  me,
  ownerUnverified = false,
  ownerUnconfirmed = false,
  onSendCustomer,
  onSendGuidance,
  onTakeover,
  onTyping,
  onGuidanceComposing,
  onRefused,
  prefill = null,
  onPrefillApplied,
}) => {
  const { t, channelLabel } = useT();
  // Optional on purpose (not useConsole, which throws): the bar is also rendered outside the console shell in tests and
  // previews, and a missing hub must mean "no hint", never a crash in the composer.
  const consoleHub = useContext(ConsoleContext)?.hub;
  const directory = useAgentStore((s) => s.directory);
  const { hasPermission } = usePermission();

  const closed = conv.state === 'CLOSED';
  const routedToTeammate = conv.state === 'CONSULTATION_REQUESTED' && conv.assignedToOther === true;
  // Private ownership in effect (wire `ownershipEnforced`), or - before PR-A2 - a live takeover by a teammate: the same
  // rule as the chat header (utils/ownership.ts lockedByOtherOwner). A recorded owner of a bot-handled row locks nobody.
  const ownedByOther = lockedByOtherOwner(conv, holder, me);
  // A human holds it and nobody can say who (no owner under private ownership, no live controller in legacy shared mode):
  // not mine to write to. Fails closed, exactly like ownedByOther.
  const ownerUnknownDuringTakeover = conv.state === 'HUMAN_TAKEOVER' && !holder;
  // The row itself says nothing about its owner (a push or draft placeholder not yet re-read, or an enforced answer that
  // named nobody and did not say "team"): not known to be mine, so no composer and no Take over, in ANY state.
  const ownerUnknown = isOwnerUnknown(conv);

  if (closed) {
    return (
      <div className="ab ab-disabled" data-testid="answer-bar-closed">
        <Icon name="lock" size={14} />
        <span>{t('ab_closed')}</span>
      </div>
    );
  }

  // A viewer who may only READ (no workspace.handle) gets no composer and no Take over, whoever owns the conversation:
  // the same first test as folders.ts `isReadOnlyFor` / `canTakeOver`, and what ChatView's header already applies to
  // Take over, Close, Let bot continue, Tags, Return to team and Spam. UX only: the server refuses the send itself.
  if (!hasPermission('workspace.handle')) {
    return (
      <div className="ab ab-disabled" role="status" data-testid="answer-bar-readonly">
        <Icon name="lock" size={14} />
        <span className="ab-ro-text">
          <span data-testid="answer-bar-no-handle">{t('ab_no_handle')}</span>
        </span>
      </div>
    );
  }

  if (ownedByOther || ownerUnknownDuringTakeover || ownerUnknown) {
    // Cosmetic only: name the owner when the directory knows them. UX only, not security. The server does NOT check
    // ownership until PR-A2 (customer send, close, subject, category); until then this gate is the only thing
    // stopping it. A stale id is never used to name anyone: only the verified owner is named.
    const nameId = holder ?? undefined;
    const ownerName = nameId ? directory[nameId] : undefined;

    // The owner is merely UNCONFIRMED: not "another agent". No composer, no Take over, and a neutral line - the
    // other-agent banner is false for the true owner during the one round trip. `ownerUnverified` is true ONLY while a
    // row refresh for this conversation is pending: once that read ends without an answer (a failure, a 404) nothing is
    // being confirmed any more, so the bar drops the "confirming" line and shows the plain read-only one below -
    // still no composer, still no Take over (unknown is not permission).
    if (ownerUnverified && !ownedByOther) {
      return (
        <div className="ab ab-disabled" role="status" data-testid="answer-bar-readonly">
          <Icon name="lock" size={14} />
          <span className="ab-ro-text">
            <span data-testid="answer-bar-confirming">{t('ab_confirming_owner')}</span>
          </span>
        </div>
      );
    }
    if ((ownerUnconfirmed || ownerUnknown) && !ownedByOther) {
      return (
        <div className="ab ab-disabled" role="status" data-testid="answer-bar-readonly">
          <Icon name="lock" size={14} />
          <span className="ab-ro-text">
            <span data-testid="answer-bar-unconfirmed">{t('ab_owner_unconfirmed')}</span>
          </span>
        </div>
      );
    }
    return (
      <div className="ab ab-disabled" role="status" data-testid="answer-bar-readonly">
        {ownerName ? (
          <span
            className="owner-chip"
            style={{ background: avatarColor(nameId) }}
            title={ownerName}
            aria-hidden="true"
          >
            {initialsOf(ownerName)}
          </span>
        ) : (
          <Icon name="lock" size={14} />
        )}
        <span className="ab-ro-text">
          <span dir="auto">
            {ownerName ? t('ab_owned_by_other', { name: ownerName }) : t('ab_handled_elsewhere')}
          </span>
          {hasPermission('workspace.viewAll') && (
            <span className="ab-ro-view" data-testid="answer-bar-readonly-view">
              {t('ab_readonly_view')}
            </span>
          )}
        </span>
      </div>
    );
  }

  const mode = answerBarMode(conv.state, holder, me);
  const toCustomer = mode === 'customer';
  // A consultation routed to a teammate who has not answered is still team shared (D3): say who it was routed to,
  // and leave the composer alone. A routed id the directory cannot name is described neutrally, never by raw id.
  const routedName = routedToTeammate && conv.assignedAgentId ? directory[conv.assignedAgentId] : undefined;
  const firstName = (customerName || '').split(' ')[0] || customerName;

  // The refusal is reported once, from the send that was refused, and then the composer shows ab_refused in
  // place of its generic failure text. Never an optimistic owner change: the owner is learned from the server.
  const sendCustomer = async (text: string, attachmentIds?: string[]) => {
    try {
      await onSendCustomer(text, attachmentIds);
    } catch (err) {
      if (isRefusal(err)) onRefused?.(conv.sessionId);
      throw err;
    }
  };
  const sendGuidance = async (text: string, attachmentIds?: string[], attachments?: MessageAttachment[]) => {
    try {
      // Text-only guidance keeps its one-argument call; pictures add the ids and their display metadata.
      if (attachmentIds && attachmentIds.length > 0) await onSendGuidance(text, attachmentIds, attachments);
      else await onSendGuidance(text);
    } catch (err) {
      if (isRefusal(err)) onRefused?.(conv.sessionId);
      throw err;
    }
  };
  const describeError = (err: unknown) => (isRefusal(err) ? t('ab_refused') : undefined);
  const composerPrefill = prefill && prefill.mode === mode ? { text: prefill.text, seq: prefill.seq } : null;
  // Always THIS conversation's id, read when the hint fires; a hub double without the method sends nothing.
  const guidanceComposing =
    onGuidanceComposing ??
    (() => {
      if (typeof consoleHub?.notifyGuidanceComposing === 'function') void consoleHub.notifyGuidanceComposing(conv.sessionId);
    });

  return (
    <div className={'ab ' + (toCustomer ? 'ab-customer' : 'ab-consult')} data-testid="answer-bar" data-mode={mode}>
      {routedToTeammate && (
        <div className="ab-routed" role="status" data-testid="answer-bar-routed">
          <Icon name="users" size={13} />
          <span dir="auto">{routedName ? t('ab_routed_to', { name: routedName }) : t('ab_routed_unknown')}</span>
        </div>
      )}
      <div className="ab-to" data-testid="answer-bar-to">
        <span className="ab-to-k">{t('ab_to')}:</span>
        {toCustomer ? (
          <span className="ab-to-v">
            <Icon name="user" size={14} />
            <span dir="auto">{customerName}</span>
            <span aria-hidden="true">·</span>
            <span>{channelLabel(conv.channel)}</span>
          </span>
        ) : (
          <span className="ab-to-v">
            <Icon name="lock" size={14} />
            {t('ab_to_bot_internal')}
          </span>
        )}
      </div>
      {/* keyed by mode: a half-typed note to the bot must never survive a flip to customer mode and be sent to a
          real person under the customer's name. A new key is a new Composer with an empty textarea. */}
      {toCustomer ? (
        <Composer
          key="customer"
          variant="customer"
          placeholder={t('composer_customer', { name: firstName })}
          onSend={sendCustomer}
          onTyping={onTyping}
          // Attachments are scoped to a session by the media service, so the composer needs it to upload at all.
          sessionId={conv.sessionId}
          sendLabel={t('ab_send_customer')}
          tone="go"
          describeError={describeError}
          prefill={composerPrefill}
          onPrefillApplied={onPrefillApplied}
          data-testid="composer-customer"
        />
      ) : (
        <Composer
          key="guidance"
          variant="guidance"
          placeholder={t('composer_guidance')}
          onSend={sendGuidance}
          // Bot voice Phase D: "still writing guidance" (throttled in the Composer). Deliberately NOT onTyping: guidance
          // must never show the customer the agent-typing dots.
          onGuidanceComposing={guidanceComposing}
          // 2026-10-20: pictures for the bot are uploaded to this conversation like any other file.
          sessionId={conv.sessionId}
          sendLabel={t('ab_send_bot')}
          tone="stop"
          describeError={describeError}
          prefill={composerPrefill}
          onPrefillApplied={onPrefillApplied}
          data-testid="composer-guidance"
        />
      )}
      {/* The take-over offer shares the hint line under the composer (operator 2026-10-12: one line less above it). */}
      <div className="ab-foot">
        <span>{t('ab_enter_hint')}</span>
        {!toCustomer && (
          <span className="ab-hint">
            {t('ab_takeover_hint')}{' '}
            <button type="button" className="ab-link" onClick={onTakeover} data-testid="answer-bar-takeover">
              {t('btn_takeover')}
            </button>
          </span>
        )}
      </div>
    </div>
  );
};
